import { useCallback, useEffect, useState } from "react";
import { useDeleteAsk, deletionRequested } from "@/lib/deleteApproval";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/DatePicker";
import { todayLocal } from "@/lib/periods";
import { listSalePayments, addSalePayment, deleteSalePayment } from "@/lib/api";

// Payments on an All Sales entry (a renewal paid in parts). The first time, what was paid on the
// sale becomes its first payment; each later payment counts as revenue in the month it came in.
export default function SalePayments({ saleId, pending, payModes, onChange }) {
  const askDelete = useDeleteAsk();
  const [rows, setRows] = useState([]);
  const [p, setP] = useState({ amount: "", date: todayLocal(), mode: "", note: "" });
  const [busy, setBusy] = useState(false);
  const cur = window.__ACTIVE_CURRENCY_SYMBOL__ || "";
  const load = useCallback(() => listSalePayments(saleId).then(setRows).catch(() => {}), [saleId]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!(Number(p.amount) > 0)) return toast.error("Enter the amount");
    if (!p.mode) return toast.error("Pick the payment mode (how they paid)");
    if (p.date > todayLocal()) return toast.error("A payment can't be dated in the future. Record it on the day the money comes in.");
    setBusy(true);
    try {
      const r = await addSalePayment(saleId, { ...p, amount: Number(p.amount) });
      toast.success("Payment added"); setP({ amount: "", date: todayLocal(), mode: "", note: "" });
      load(); onChange(r.sale);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add the payment"); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    const reason = askDelete("this payment", "Remove this payment?");
    if (reason === null) return;
    try { const r = await deleteSalePayment(saleId, id, reason); if (deletionRequested(r)) return; load(); onChange(r.sale); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove it"); }
  };
  return (
    <div className="rounded-lg border border-[#262626] p-3 space-y-2" data-testid="sale-payments">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Payments {pending > 0 && <span className="normal-case tracking-normal text-amber-300">· balance {cur}{Number(pending).toLocaleString()}</span>}</div>
      {rows.map((r) => (
        <div key={r.id} className="flex items-center gap-2 text-sm">
          <span className="font-mono text-zinc-400 w-24">{r.date}</span>
          <span className="font-mono">{cur}{Number(r.amount).toLocaleString()}</span>
          <span className="text-xs text-zinc-500">{r.mode}{r.note ? ` · ${r.note}` : ""}</span>
          {r.note !== "First payment (from the sale)" && (
            <button type="button" onClick={() => remove(r.id)} className="ml-auto text-zinc-600 hover:text-rose-400" aria-label="Remove payment"><Trash2 className="h-3.5 w-3.5" /></button>
          )}
        </div>
      ))}
      {pending > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Input type="number" value={p.amount} onChange={(e) => setP({ ...p, amount: e.target.value })} placeholder="Amount" className="bg-[#0a0a0a] border-[#262626] font-mono w-28" data-testid="sale-pay-amount" />
          <div className="w-36"><DatePicker value={p.date} onChange={(e) => setP({ ...p, date: e.target.value })} /></div>
          <select value={p.mode} onChange={(e) => setP({ ...p, mode: e.target.value })} className="bg-[#0a0a0a] border border-[#262626] rounded-md px-2 py-1.5 text-sm" data-testid="sale-pay-mode">
            <option value="">Mode</option>
            {payModes.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <Input value={p.note} onChange={(e) => setP({ ...p, note: e.target.value })} placeholder="Note" className="bg-[#0a0a0a] border-[#262626] w-32" />
          <Button type="button" size="sm" onClick={add} disabled={busy} className="bg-blue-600 hover:bg-blue-500" data-testid="sale-pay-add">Add payment</Button>
        </div>
      )}
      {!rows.length && pending > 0 && <div className="text-[11px] text-zinc-500">When the rest comes in, add it here. It counts in the month it's paid.</div>}
    </div>
  );
}
